import { Stack, Tab, Tabs, Typography, Link } from "@mui/material";
import { useLocation, useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { createColumnHelper } from "@tanstack/react-table";
import {
  CustomTable,
  CustomTabPanel,
  DeleteModal,
  Loader,
  PageListLayout,
} from "../../../shared";
import { useTipsAndTricks } from "../hooks/useTipsAndTricks";
import TipsAndProcedureBasicDetails from "../components/TipsAndProcedureBasicDetails";

export const TipsAndProcedureDetails = () => {
  let { id } = useParams();
  const {
    tipsAndTricksDetails,
    handleVideoClick,
    tipsAndTricks,
    onImageClick,
    onDelete,
    deleteInfo,
    resetDeleteInfo,
    confirmDelete,
  } = useTipsAndTricks({
    isList: false,
    tipId: id,
  });

  const navigate = useNavigate();
  const { search } = useLocation();
  const handleChange = (event, newValue) => {
    navigate(`/tips-tricks/details/${id}${newValue}`);
  };

  if (tipsAndTricksDetails?.isLoading) {
    return <Loader />;
  }

  const breadcrumbs = [
    <Link
      underline="none"
      key="1"
      color="#1760D2"
      onClick={(e) => navigate("/tips-tricks")}
      sx={{ fontSize: "14px", fontWeight: 400 }}
    >
      Tips & Procedures
    </Link>,
    <Typography
      key="2"
      color="inherit"
      sx={{ fontSize: "14px", fontWeight: 400 }}
    >
      {tipsAndTricksDetails?.data?.name}
    </Typography>,
  ];
  const columnHelper = createColumnHelper();

  const recipeColumns = [
    columnHelper.accessor("recipe_name", {
      header: () => "Recipes",
    }),
    columnHelper.display({
      id: "actions",
      cell: (props) => {
        return (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            gap="8px"
          >
            <img alt="details" src="/icons/arrow_forward.png" />
          </Stack>
        );
      },
    }),
  ];

  const categoryColumns = [
    columnHelper.accessor("category_name", {
      header: () => "Categories",
    }),
  ];

  return (
    <>
      <PageListLayout
        breadcrumbs={breadcrumbs}
        headerText={tipsAndTricksDetails?.data?.name}
        rightContent={
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
          >
            <img
              alt="edit"
              onClick={(e) => navigate(`/tips-tricks/edit/${id}`)}
              src="/icons/ic_edit.png"
              style={{ cursor: "pointer" }}
            />
            <img
              alt="delete"
              onClick={() => onDelete(id, "/tips-tricks")}
              src="/icons/ic_trash_sm.png"
              style={{ cursor: "pointer" }}
            />
          </Stack>
        }
        tabs={
          <Tabs value={search} onChange={handleChange}>
            <Tab label="Basic Details" value="?basic-details" />
            <Tab label="Recipes" value="?recipes" />
            <Tab label="Categories" value="?categories" />
          </Tabs>
        }
        sx={{
          px: "16px",
          minHeight: "calc(100vh - 145px)",
          backgroundColor: "#ffffff",
        }}
      >
        <CustomTabPanel value={search} index="?basic-details" border={false}>
          <TipsAndProcedureBasicDetails
            tipsAndTricksDetails={tipsAndTricksDetails}
            handleVideoClick={handleVideoClick}
            tipsAndTricks={tipsAndTricks}
            onImageClick={onImageClick}
          />
        </CustomTabPanel>
        <CustomTabPanel value={search} index="?recipes" border={false}>
          <CustomTable
            defaultData={tipsAndTricksDetails?.data?.recipes ?? []}
            columns={recipeColumns}
            headerText="Recipes"
            isLoading={tipsAndTricksDetails?.isLoading}
            onRowLink={(e) => {
              return `/recipe/details/${e.recipe_id}?basic-details`;
            }}
            tableHeaderStyles={{ fontSize: "18px", fontWeight: 500, px: 0 }}
            enableTableContainerPaddingBottom
          />
        </CustomTabPanel>
        <CustomTabPanel value={search} index="?categories" border={false}>
          <CustomTable
            defaultData={tipsAndTricksDetails?.data?.categories ?? []}
            columns={categoryColumns}
            headerText="Categories"
            isLoading={tipsAndTricksDetails?.isLoading}
            tableHeaderStyles={{ fontSize: "18px", fontWeight: 500, px: 0 }}
            enableTableContainerPaddingBottom
          />
        </CustomTabPanel>
        <DeleteModal
          open={deleteInfo.show}
          handleClose={() => resetDeleteInfo()}
          onConfirm={() => confirmDelete()}
        />
      </PageListLayout>
    </>
  );
};
